import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {isFlowchartType} from 'processes/processes-platform/FeatureOptions';
import {TaskNodeDetailsSidepanelContentProps} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {useAllSchemasById} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useAllSchemasById';
import {
  ProcessTaskSchemaEditorContent,
  ProcessTaskSchemaEditorContentSections,
} from 'processes/settings/ProcessTaskSchemaEditor';
import {ProcessTaskSchema} from 'processes/settings/ProcessTaskSchemaUtils';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import InputOutputFieldTables from './InputOutputFieldTables';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';
import {cacheUpdatedTaskSchemaAndUpdateNode} from './nodeUtils';
import TransitionFieldInfoSection from './TransitionFieldInfoSection';

export enum NodeSettingsTabKeys {
  FLOWCHART_SETTINGS = 'FLOWCHART_SETTINGS',
  NODE_FIELD_DETAILS = 'NODE_FIELD_DETAILS',
  TASK_SCHEMA_DETAILS = 'TASK_SCHEMA_DETAILS',
}

export function TaskSchemaDetailsTab(props: TaskNodeDetailsSidepanelContentProps): JSX.Element {
  const context = NodeDetailsSidepanelContext.useContext();
  // TODO(BNCH-126759): Update types to better handle when selected node is task vs logic node
  const selectedNode = context.selectedNode as TaskSchemaNode;

  const {
    processTaskSchemas: allTaskSchemas,
    storableSchemas,
    tagSchemas,
    resultSchemas,
    statusLifecycles,
    haveSchemasLoaded,
  } = useAllSchemasById(context.flowchartTaskSchema);

  const taskSchemaId = selectedNode.data.taskSchemaId;
  const baseTaskSchema = context.allSchemasById[
    taskSchemaId
  ] as ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  const taskSchema: ProcessTaskSchema = {
    ...baseTaskSchema,
    relevantForms: [],
    flowchartConfig: null,
  };

  return !haveSchemasLoaded ? (
    <CenteredHorizontalThrobber />
  ) : (
    <div
      className={css(styles.taskSchemaEditorContainer)}
      {...getTestProps({component: 'TaskSchemaDetailsTab', element: 'root'})}
    >
      <ProcessTaskSchemaEditorContent
        processTaskSchema={taskSchema}
        storableSchemas={storableSchemas}
        tagSchemas={tagSchemas}
        taskSchemas={
          // We do not need flowchart type task schemas here because they are not needed for basic task configuration
          allTaskSchemas
            ?.filter((schema) => !isFlowchartType(schema.executionType))
            .map((schema) => ({...schema, flowchartConfig: null})) ?? []
        }
        statusLifecycles={statusLifecycles}
        resultSchemas={resultSchemas}
        isAnyPartOfFlowchartConfigInaccessible={null}
        sectionsToHide={[
          ProcessTaskSchemaEditorContentSections.TASK_FIELDS,
          ProcessTaskSchemaEditorContentSections.OUTPUT,
          ProcessTaskSchemaEditorContentSections.FLOWCHART,
        ]}
        afterTaskSchemaUpdate={(newTaskSchema) => {
          cacheUpdatedTaskSchemaAndUpdateNode(
            newTaskSchema,
            selectedNode,
            context.onUpdateTaskDefinitionVersion
          );
        }}
        locked={props.locked}
      />
    </div>
  );
}

export function NodeFieldDetailsTab(props: TaskNodeDetailsSidepanelContentProps): JSX.Element {
  const context = NodeDetailsSidepanelContext.useContext();

  return (
    <div className={css(styles.tabContentContainer)}>
      {props.selectedFieldId != null ? (
        <TransitionFieldInfoSection
          selectedFieldId={props.selectedFieldId}
          node={context.selectedNode as TaskSchemaNode}
          flowchart={context.interactiveFlowchart}
          allSchemasById={context.allSchemasById}
          flowchartTaskSchema={context.flowchartTaskSchema}
          customMappingDataByEdgeId={context.customMappingDataByEdgeId}
          onChangeCustomMappingData={context.onChangeCustomMappingData}
          onChangeSelectedFieldId={props.onChangeSelectedFieldId}
          transitionSourceByNodeIdPair={context.transitionSourceByNodeIdPair}
          locked={props.locked}
          validationErrors={props.validationErrors}
        />
      ) : (
        <InputOutputFieldTables
          selectedNode={context.selectedNode}
          interactiveFlowchart={context.interactiveFlowchart}
          allSchemasById={context.allSchemasById}
          flowchartTaskSchema={context.flowchartTaskSchema}
          flowchartEditorConfigurator={context.flowchartEditorConfigurator}
          onChangeSelectedFieldId={props.onChangeSelectedFieldId}
          locked={props.locked}
          refetchFlowchartConfig={context.refetchFlowchartConfig}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  tabContentContainer: {
    padding: '36px',
  },
  noFieldSelectedText: {
    fontSize: '16px',
    color: Color.GRAY_900,
  },
  taskSchemaEditorContainer: {
    padding: '0px 16px',
  },
});
